import axios from 'axios';
import { env } from '@/lib/env';

/**
 * Transformations API — /backend/api/dashboard/aura (already on API Gateway).
 * Dashboard BFF: CRUD → databasehandler; analyze/agents/runs → master.
 * Do not use /agent-studio: gateway POST requires agent-studio.create (403).
 *
 * If BACKEND_URL is unset, fall back to the master agent (local :8080).
 */
function joinBackend(root, suffix) {
  if (root.endsWith('/backend')) return `${root}/api/${suffix}`;
  if (root.endsWith('/api')) return `${root}/${suffix}`;
  return `${root}/api/${suffix}`;
}

function backendRoot() {
  return (env('REACT_APP_BACKEND_URL') || '').replace(/\/$/, '');
}

function orchestratorRoot() {
  return (env('REACT_APP_ORCHESTRATOR_URL') || '').replace(/\/$/, '');
}

function isDashboardAuraBackend(root) {
  if (!root) return false;
  // Real BFF: API Gateway /backend or dashboardapi :8002.
  // Local REACT_APP_BACKEND_URL=http://localhost:8003 is only the auth-bypass dummy.
  if (root.includes('/backend') || root.includes('dashboard')) return true;
  try {
    const { hostname, port } = new URL(root);
    const local = hostname === 'localhost' || hostname === '127.0.0.1';
    return local && port === '8002';
  } catch {
    return false;
  }
}

export function getAuraApiBase() {
  const root = backendRoot();
  const orchestrator = orchestratorRoot();
  if (isDashboardAuraBackend(root)) return joinBackend(root, 'dashboard/aura');
  if (orchestrator) return `${orchestrator}/api/aura`;
  if (root) return joinBackend(root, 'dashboard/aura');
  return '';
}

export function getAuraAnalysisApiBase() {
  return getAuraApiBase();
}

export const AURA_API = getAuraApiBase();
const AURA_ANALYSIS_API = AURA_API;

/** Bootstrap company code for v1 seed (still listed among companies). */
export const DEFAULT_AURA_COMPANY_CODE = 'RTS';

export async function getDefaultAuraCompany() {
  const { data } = await axios.get(`${AURA_API}/companies/default`);
  return data;
}

export async function listAuraCompanies(limit = 100) {
  const { data } = await axios.get(`${AURA_API}/companies`, { params: { limit } });
  return Array.isArray(data) ? data : [];
}

export async function createAuraCompany({
  code,
  name,
  description = '',
  status = 'active',
}) {
  const { data } = await axios.post(`${AURA_API}/companies`, {
    code,
    name,
    description,
    status,
  });
  return data;
}

export async function updateAuraCompany(companyId, { name, description, status } = {}) {
  const body = {};
  if (name !== undefined) body.name = name;
  if (description !== undefined) body.description = description;
  if (status !== undefined) body.status = status;
  const { data } = await axios.patch(
    `${AURA_API}/companies/${encodeURIComponent(companyId)}`,
    body,
  );
  return data;
}

export async function deleteAuraCompany(companyId) {
  const { data } = await axios.delete(
    `${AURA_API}/companies/${encodeURIComponent(companyId)}`,
  );
  return data;
}

export async function getAuraCompany(companyId) {
  const { data } = await axios.get(
    `${AURA_API}/companies/${encodeURIComponent(companyId)}`,
  );
  return data;
}

export async function listAuraProjects(
  limit = 100,
  { companyId, companyCode } = {},
) {
  const params = { limit };
  if (companyId) params.company_id = companyId;
  else if (companyCode) params.company_code = companyCode;
  const { data } = await axios.get(`${AURA_API}/projects`, { params });
  return Array.isArray(data) ? data : [];
}

export async function createAuraProject({
  name,
  description = '',
  created_by,
  company_id,
  company_code,
  status,
}) {
  const body = {
    name,
    description,
    created_by: created_by || undefined,
  };
  if (company_id) {
    body.company_id = company_id;
  } else if (company_code) {
    body.company_code = company_code;
  }
  if (status) body.status = status;
  const { data } = await axios.post(`${AURA_API}/projects`, body);
  return data;
}

export async function updateAuraProject(projectId, payload = {}) {
  const { data } = await axios.patch(
    `${AURA_API}/projects/${encodeURIComponent(projectId)}`,
    payload,
  );
  return data;
}

export async function deleteAuraProject(projectId) {
  const { data } = await axios.delete(
    `${AURA_API}/projects/${encodeURIComponent(projectId)}`,
  );
  return data;
}

export async function getAuraProject(projectId) {
  const { data } = await axios.get(
    `${AURA_API}/projects/${encodeURIComponent(projectId)}`,
  );
  const out = normalizeAuraProjectDetail(data, projectId);
  if (out.project && !out.company && out.project.company_id) {
    try {
      out.company = await getAuraCompany(out.project.company_id);
    } catch {
      out.company = null;
    }
  }
  if (out.project && !data?.project) {
    const id = encodeURIComponent(projectId);
    try {
      const { data: runRows } = await axios.get(`${AURA_API}/projects/${id}/runs`);
      out.runs = Array.isArray(runRows) ? runRows : [];
    } catch {
      out.runs = [];
    }
    try {
      const { data: artifactRows } = await axios.get(
        `${AURA_API}/projects/${id}/artifacts`,
      );
      out.artifacts = Array.isArray(artifactRows) ? artifactRows : [];
    } catch {
      out.artifacts = [];
    }
  }
  return out;
}

/** Master returns {project, company, runs, artifacts}; dbhandler used to return a flat row. */
export function normalizeAuraProjectDetail(data, projectId) {
  if (data?.project) {
    return {
      project: data.project,
      company: data.company || null,
      runs: Array.isArray(data.runs) ? data.runs : [],
      artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
    };
  }
  if (data?.id) {
    return {
      project: data,
      company: data.company || null,
      runs: Array.isArray(data.runs) ? data.runs : [],
      artifacts: Array.isArray(data.artifacts) ? data.artifacts : [],
    };
  }
  return {
    project: null,
    company: null,
    runs: [],
    artifacts: [],
    projectId,
  };
}

export async function analyzeOffline(projectId, file, { created_by } = {}) {
  const form = new FormData();
  form.append('file', file);
  if (created_by) form.append('created_by', created_by);
  const { data } = await axios.post(
    `${AURA_ANALYSIS_API}/projects/${encodeURIComponent(projectId)}/analyze-offline`,
    form,
    {
      timeout: 60_000,
    },
  );
  return data;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Poll project runs until the offline job finishes (API Gateway cannot wait on the LLM). */
export async function waitForAuraOfflineRun(
  projectId,
  runId,
  { timeoutMs = 300_000, intervalMs = 2500 } = {},
) {
  const start = Date.now();
  let last = null;
  while (Date.now() - start < timeoutMs) {
    const data = await getAuraProject(projectId);
    last = data;
    const run = (data.runs || []).find((r) => r.id === runId);
    const status = String(run?.status || '').toLowerCase();
    if (status === 'succeeded' || status === 'failed') {
      return { ...data, run };
    }
    await sleep(intervalMs);
  }
  const err = new Error(
    'Analysis is still running. Refresh this project in a minute to see results.',
  );
  err.project = last;
  throw err;
}

export async function getAuraRun(runId) {
  const { data } = await axios.get(
    `${AURA_ANALYSIS_API}/runs/${encodeURIComponent(runId)}`,
  );
  return data;
}

export async function chatAuraProject(projectId, question, history = []) {
  const base = getAuraAnalysisApiBase();
  if (!base) {
    throw new Error('Transformations API is not configured. Set REACT_APP_ORCHESTRATOR_URL.');
  }
  const { data } = await axios.post(
    `${base}/projects/${encodeURIComponent(projectId)}/chat`,
    {
      question,
      history: (history || [])
        .filter((t) => t?.role && t?.content)
        .map((t) => ({ role: t.role, content: String(t.content) }))
        .slice(-8),
    },
    { timeout: 120_000 },
  );
  return data;
}

export async function listAuraAgents() {
  const { data } = await axios.get(`${AURA_ANALYSIS_API}/agents`);
  return data;
}

export async function updateAuraAgentModel(agentId, modelId) {
  const { data } = await axios.put(
    `${AURA_ANALYSIS_API}/agents/${encodeURIComponent(agentId)}/model`,
    { model_id: modelId },
  );
  return data;
}

export async function resetAuraAgentModel(agentId) {
  const { data } = await axios.delete(
    `${AURA_ANALYSIS_API}/agents/${encodeURIComponent(agentId)}/model`,
  );
  return data;
}

export function shortAuraModelLabel(modelId) {
  if (!modelId) return 'Default';
  if (modelId.includes('opus-4-8')) return 'Claude Opus 4.8';
  if (modelId.includes('opus')) return 'Claude Opus';
  if (modelId.includes('sonnet-4-5')) return 'Claude Sonnet 4.5';
  if (modelId.includes('sonnet-4')) return 'Claude Sonnet 4';
  const parts = String(modelId).split('.');
  return parts[parts.length - 1] || modelId;
}

export function statusLabel(status) {
  const s = String(status || 'draft').toLowerCase();
  if (s === 'in_progress') return 'In progress';
  if (s === 'discovery') return 'Discovery';
  if (s === 'draft') return 'Draft';
  if (s === 'complete' || s === 'completed') return 'Complete';
  return s.replace(/_/g, ' ');
}
